import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { getUserColumns } from '@/helpers';
import { UsersFilters } from '@/pages/Users/UsersFilters';
import { useDeleteUserMutation, useGetAllUsers } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { useFilterStore } from '@/store';
import { AllowedPermissions, SelectOptionProps, TableData, User } from '@/types';

export const Users = () => {
  const [t] = useTranslation('common');
  const [search, setSearch] = useState<string>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const { currentUserData: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);

  const [filters, setFilters] = useState<{
    role: SingleValue<SelectOptionProps>;
    status: SingleValue<SelectOptionProps>;
    organization: SingleValue<SelectOptionProps>;
  }>({
    role: null,
    status: null,
    organization: null
  });

  const { organizationFilter } = useFilterStore();

  const [isFilterModalVisible, setIsFilterModalVisible] = useState<boolean>(false);

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_USER,
    editPermission: PERMISSION_TYPE.UPDATE_USER,
    deletePermission: PERMISSION_TYPE.DELETE_USER
  };

  const getOrganizationValue = () => {
    if (organizationFilter) {
      return organizationFilter.value as number;
    }
    if (filters.organization === null && currentUser?.organizations.length === 1) {
      return currentUser?.organizations[0].id as number;
    }
    return filters.organization?.value as number;
  };

  const {
    isLoading,
    data: allUsers,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetAllUsers(
    PAGE_SIZE,
    search,
    filters.role?.value as number,
    getOrganizationValue(),
    filters.status?.value as boolean
  );

  const { mutate: deleteUser } = useDeleteUserMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      refetch();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleCreate = () => {
    navigate('/users/create');
  };

  const handleEdit = (user: User) => {
    navigate(`/users/${user.id}/edit`);
  };

  const handleFilterModalVisibility = () => {
    setIsFilterModalVisible(!isFilterModalVisible);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalVisible(false);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedUser(undefined);
  };

  const handleDeleteUser = (row: User) => {
    setIsOpenDeleteModal(true);
    setSelectedUser(row);
  };

  const resetFilters = () => {
    setIsFilterModalVisible(false);
    setFilters({ role: null, status: null, organization: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: Record<string, SelectOptionProps>) => {
    setIsFilterModalVisible(false);
    setFilters({
      role: values.filterByRole,
      status: values.filterByStatus,
      organization: values.filterByOrganization
    });
  };

  const handleViewDetails = (row: User) => {
    navigate('/users/' + row.id);
  };

  const formatData = (): TableData[] => {
    return allUsers?.pages.flatMap((page) =>
      page.data?.items.map((user: User) => ({
        ...user,
        organizations:
          user?.organizations?.length === 0
            ? [{ id: undefined, name: t('allOrganizations') }]
            : user?.organizations?.map((org) => ({ id: org.id, name: org.name }))
      }))
    ) as TableData[];
  };

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allUsers && (
            <Table
              data={formatData()}
              columns={getUserColumns()}
              testIdPrefix={TestIds.users.prefix}
              tableTitle={t('users')}
              addButtonLabel={t('addUser')}
              permissions={permissions}
              search={searchCallback}
              handleCreate={handleCreate}
              handleEdit={(item) => handleEdit(item as User)}
              handleDelete={(item) => handleDeleteUser(item as User)}
              handleViewDetails={(item) => handleViewDetails(item as User)}
              handleOpenFilterModal={handleFilterModalVisibility}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              numberOfAppliedFilters={appliedFiltersCounter}
              hiddenColumnsMobile={['enabled']}
              mobileColumnOffset={4}
            />
          )}
          {selectedUser && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedUser.firstName} ${selectedUser.lastName}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => {
                selectedUser && selectedUser.id && deleteUser(selectedUser.id);
              }}
            />
          )}
          {isFilterModalVisible && (
            <UsersFilters
              apply={applyFilters}
              reset={resetFilters}
              onClose={handleCloseFilterModal}
              filterByStatus={filters.status}
              filterByRole={filters.role}
              filterByOrganization={filters.organization}
            />
          )}
        </div>
      )}
    </div>
  );
};
